import { get, last, zip } from 'lodash-es'
import { formatSI } from './format-si'

// Generic
export const assert = (condition, message) => {
  if (!condition) {
    throw new Error('Assertion failed: ' + message)
  }
}
export const prop = key => object => get(object, key)
export const partial = (fn, ...args) => (...rest) => fn(...args, ...rest) // perf: Avoid Lodash partial
export const hasDuplicates = array => new Set(array).size !== array.length
export const unique = array => [...new Set(array)] // Substantially better performance than filter(distinct)
export const toNumber = value => !value || isNaN(Number(value)) ? value : Number(value)
export const asNumber = value => value ?? 0

// Threading

/**
 * Threads values through a sequence of functions.
 * Inserts threaded value as the first argument to the subsequent function.
 *
 * @param value Initial value
 * @param fnsAndArgs Array of fns with args
 * @returns Result of the threading
 */
export const threadFirst = (value, fnsAndArgs) => {
  if (!fnsAndArgs?.length) {
    return value
  }
  const { fn, args } = getThreadingFnAndArgs(fnsAndArgs)
  const nextValue = fn(value, ...args)
  return threadFirst(nextValue, fnsAndArgs.slice(1))
}

/**
 * Threads values through a sequence of functions.
 * Inserts threaded value as the last argument to the subsequent function.
 *
 * @param value Initial value
 * @param fnsAndArgs Array of fns with args
 * @returns Result of the threading
 */
export const thread = (value, fnsAndArgs) => {
  if (!fnsAndArgs?.length) {
    return value
  }
  const { fn, args } = getThreadingFnAndArgs(fnsAndArgs)
  const nextValue = fn(...args, value)
  return thread(nextValue, fnsAndArgs.slice(1))
}

const getThreadingFnAndArgs = fnsAndArgs => {
  const fnAndArgs = fnsAndArgs[0]
  const isArray = Array.isArray(fnAndArgs)
  const fn = isArray ? fnAndArgs[0] : fnAndArgs
  const args = isArray ? fnAndArgs.slice(1) : []
  return { fn, args }
}

// Predicates
export const nonNull = value => value != null
export const distinct = (item, index, array) => array.indexOf(item) === index
export const distinctBy = getter => (item, index, array) =>
  array.findIndex(other => getter(other) === getter(item)) === index

// Reducers
export const minimum = (acc, value) => value == null ? acc : Math.min(acc, value)

// Unit conversions
export const toKmh = ms => ms && Math.round(ms * 3.6)
export const toMs = kmh => kmh && kmh / 3.6

// Rounding
const genericPrecision = 10_000_000
export const aboutSame = (a, b) => Math.abs(a - b) < 1 / genericPrecision
export const aboutZero = value => !value || aboutSame(value, 0)
export const createRound = precision => value => value && Math.round(value * precision) / precision
export const toPrecision = precision => value => Number(Number(value).toPrecision(precision))
const round = createRound(1)
export const display = value => value == null ? '' : value
export const roundDisplay = value => display(round(value))
export const withUnit = (value, unit) => value == null || value === '' ? '' : `${value} ${unit}`
export const roundGeneric = createRound(genericPrecision)
// - Distance
const distancePrecision = 1_000
export const roundDistance = createRound(distancePrecision)
export const sameDistance = (distance1, distance2) => Math.abs(distance2 - distance1) < 1 / distancePrecision
// - Velocity
const velocityPrecision = 100
export const roundVelocity = createRound(velocityPrecision)
// - SI value
export const formatSIValue = (value, unit = '', prefix = '', suffix = '') =>
  prefix + formatSI(value) + unit + suffix
// - Length measurement
const measurementDecimalPlaces = 2
export const roundLengthMeasurement = value => value && Number(value.toFixed(measurementDecimalPlaces))
// - Zoom
const zoomPrecision = 1_000
export const roundZoom = createRound(zoomPrecision)
// - Coords
const coordsPrecision = 100
export const roundCoords = createRound(coordsPrecision)
// Battery state of charge
const batteryStateOfChargePrecision = 10
export const roundBatteryStateOfCharge = createRound(batteryStateOfChargePrecision)

// Objects
export const setDefined = (object, data) => {
  Object.entries(data)
    .filter(([_, value]) => value !== undefined)
    .forEach(([key, value]) => {
      object[key] = value
    })
}

// Arrays
export const withoutLastNulls = array =>
  !array.length || last(array) != null ? array : withoutLastNulls(array.slice(0, -1))
export const cycleToNext = (array, value) => array[(array.indexOf(value) + 1) % array.length]

export const withoutLast = array => array.slice(0, -1)
export const withoutFirst = array => array.slice(1)

export const stripLeft = (array, length) =>
  array.splice(Math.max(0, array.length - length), array.length)

export const replaceArrayElement = (array, index, element) => array.toSpliced(index, 1, element)

export const transposeMatrix = ([...arrays]) => zip(...arrays)

export const accumulate = array =>
  array.reduce((acc, value, index) => [...acc, index > 0 ? acc[index - 1] + value : value], [])
export const accumulatedIndex = (array, pointer) => array.findIndex(value => value >= pointer)

export const breakpointIndex = (breakpoints, value) =>
  !breakpoints.length
    ? 0
    : value >= last(breakpoints)
      ? breakpoints.length
      : breakpoints.findIndex(breakpoint => breakpoint > value)

// Units
export const metersToKm = meters => meters / 1_000
export const mpsToKph = mps => mps * 3.6

// Timing
export const timeNow = () => window.performance.now()
export const getDuration = before => Math.round(timeNow() - before)
export const time = async (fn, callback) => {
  const before = timeNow()
  let exception
  try {
    return await fn()
  } catch (error) {
    exception = error
    throw error
  } finally {
    callback(getDuration(before), exception)
  }
}
export const timeSync = (fn, callback) => {
  const before = timeNow()
  let exception
  try {
    return fn()
  } catch (error) {
    exception = error
    throw error
  } finally {
    callback(getDuration(before), exception)
  }
}
export const throttle = (fn, wait) => {
  let last
  return (...args) => {
    const now = timeNow()
    if (last == null || now - last > wait) {
      last = now
      return fn(...args)
    }
  }
}

// const timeStats = (name, fn) => {
//   window.mathrailEditorLoggingStats = window.mathrailEditorLoggingStats ?? {}
//   const stats = window.mathrailEditorLoggingStats
//
//   const [duration, result] = timed(fn)
//
//   stats[name] = stats[name] ?? []
//   stats[name].push(duration)
//   console.log(`Time stats ${name} @`, roundTimeStats(duration),
//     'avg', roundTimeStats(mean(stats[name])), 'from', stats[name].length)
//
//   return result
// }
// const roundTimeStats = toPrecision(3)

export const byteArrayToBase64 = byteArray => window.btoa(String.fromCharCode(...byteArray))
export const base64ToByteArray = byteArrayBase64 => [...window.atob(byteArrayBase64)].map(c => c.charCodeAt(0))
